import { useState, useCallback, useRef, KeyboardEvent, forwardRef, useImperativeHandle } from "react";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

export interface PromptInputHandle {
  /** Submit any pending input text. Returns the text if flushed, null if empty. */
  flush: () => Promise<string | null>;
}

interface PromptInputProps {
  onAdd: (text: string) => boolean | Promise<boolean>;
  onClearAll: () => void;
  hasPrompts: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
}

export const PromptInput = forwardRef<PromptInputHandle, PromptInputProps>(
  function PromptInput({ onAdd, onClearAll, hasPrompts, disabled, autoFocus }, ref) {
    const [value, setValue] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const submittingRef = useRef(false);
    const isDisabled = disabled || isSubmitting;

    const submit = useCallback(async (): Promise<string | null> => {
      if (disabled || submittingRef.current) return null;
      const trimmed = value.trim();
      if (!trimmed) return null;
      submittingRef.current = true;
      setIsSubmitting(true);
      try {
        const accepted = await onAdd(trimmed);
        if (!accepted) return null;
        setValue("");
        return trimmed;
      } finally {
        submittingRef.current = false;
        setIsSubmitting(false);
      }
    }, [value, onAdd, disabled]);

    useImperativeHandle(ref, () => ({ flush: submit }), [submit]);

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        void submit();
      }
    };

    return (
      <div className="flex items-center gap-2">
        <div className="flex-1 flex items-center gap-2 rounded-lg bg-white/5 border border-white/10 px-3 py-2 focus-within:border-white/20 transition-colors">
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isDisabled ? "Processing..." : "Describe a sound..."}
            disabled={isDisabled}
            autoFocus={autoFocus}
            className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none disabled:opacity-50"
          />
          <button
            onClick={() => void submit()}
            disabled={!value.trim() || isDisabled}
            className={cn(
              "h-7 w-7 flex items-center justify-center rounded-md transition-all",
              value.trim() && !isDisabled
                ? "bg-white/10 hover:bg-white/20 text-foreground"
                : "text-muted-foreground/50"
            )}
          >
            {isDisabled ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
          </button>
        </div>
        {hasPrompts && (
          <button
            onClick={onClearAll}
            className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-white/10 text-muted-foreground hover:text-foreground transition-all"
            title="Clear all prompts"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  }
);
